import { Button, Toaster, Tooltip } from "@pej/kore/components";
import type { QueryClient } from "@tanstack/react-query";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { createRootRouteWithContext, Outlet, useRouter } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { ApiError } from "@/api/error";

interface RouterContext {
    queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
    component: RootComponent,
});

function RootComponent() {
    // Two attributes so one theme flip drives the app's `.dark`-keyed tokens
    // and kore's `data-theme`-keyed brand override in lockstep.
    return (
        <ThemeProvider attribute={["class", "data-theme"]} defaultTheme="system" disableTransitionOnChange>
            <Tooltip.Provider delay={500} closeDelay={100}>
                <Outlet />
                <Toaster richColors position="top-right" />
            </Tooltip.Provider>
        </ThemeProvider>
    );
}

export function RootErrorComponent({ error, reset }: ErrorComponentProps) {
    const router = useRouter();
    const apiError = error instanceof ApiError ? error : null;
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-muted-foreground">{apiError ? (apiError.detail ?? apiError.code) : String(error)}</p>
            {apiError?.requestId && <p className="text-xs text-muted-foreground">Request ID: {apiError.requestId}</p>}
            <div className="flex gap-2">
                <Button variant="outline" onClick={() => reset()}>
                    Try again
                </Button>
                <Button onClick={() => router.navigate({ to: "/" })}>Go home</Button>
            </div>
        </div>
    );
}
